import React from 'react';
import { useBasicInfo } from '@/components/_shared/contexts/BasicInfoContext';
import { useIntl } from 'react-intl';
import styles from './GeneralEventPanelInfo.module.scss';
import {
  getDisplayTimezone,
  getSimpleFormattedDateForTwoDays,
  getSingleDateDisplayString,
  isPastMidnightSingleNightEvent,
  isSameDayInTimezone,
} from '@/utils/dateTime';
import { ExploreSimilarEventsButton } from '../ExploreSimilarEventsButton';
import { useDisplayStatus } from '@/components/_shared/contexts/DisplayStatusContext';
import { CONVERSION_BAR_MESSAGES } from '../constants';
import { SalesStartSoonPanel } from './SalesStartSoonPanel/SalesStartSoonPanel';
import { CheckoutButton } from '../CheckoutButton/CheckoutButton';
import { HighLevelStatus } from '@/utils/highLevelStatus';
import { useAttendeeTimezone } from '@/hooks/useAttendeeTimezone';

type GeneralEventPanelInfoProps = {
  showSalesEndedDonationPanel?: boolean;
};

export const GeneralEventPanelInfo: React.FC<GeneralEventPanelInfoProps> = ({
  showSalesEndedDonationPanel = false,
}) => {
  const { formatMessage, locale } = useIntl();
  const { hideStartDate, hideEndDate, startDate, endDate, isOnline, isParentEvent } = useBasicInfo();
  const { displayStatus } = useDisplayStatus();
  const attendeeTimezone = useAttendeeTimezone(isOnline ?? false);

  const label = CONVERSION_BAR_MESSAGES[displayStatus] ?? null;

  if (displayStatus === HighLevelStatus.SALES_START_SOON) {
    return <SalesStartSoonPanel />;
  }

  const isSingleNightEvent = !isParentEvent && isPastMidnightSingleNightEvent(startDate, endDate);
  const showEndTimeOnly = !showSalesEndedDonationPanel && hideStartDate && !hideEndDate && endDate?.utc;
  const dateToShow = isSingleNightEvent ? startDate : showSalesEndedDonationPanel ? endDate : startDate;
  const showTime = isSingleNightEvent ? !hideStartDate : showSalesEndedDonationPanel ? !hideEndDate : !hideStartDate;
  const timezone = getDisplayTimezone({
    isOnlineEvent: isOnline ?? false,
    eventTimezone: dateToShow?.timezone,
    attendeeTimezone,
  });
  const useBulletSeparator = !showEndTimeOnly && showTime;
  const isSameDay = (() => {
    try {
      return isSameDayInTimezone(startDate?.utc ?? '', endDate?.utc ?? '', timezone);
    } catch (error) {
      console.error('Error calculating same day in timezone:', error);
      return true;
    }
  })();

  const dateString = isParentEvent
    ? ''
    : !isSameDay && !isSingleNightEvent
      ? getSimpleFormattedDateForTwoDays(startDate, endDate, locale, timezone)
      : dateToShow
        ? getSingleDateDisplayString(
            dateToShow,
            showTime,
            locale,
            timezone,
            formatMessage,
            false,
            useBulletSeparator ? ' • ' : undefined,
            true,
            isOnline ?? false,
          )
        : undefined;

  return (
    <div className={styles.container} data-testid="general-event-panel-info">
      <div className={styles.content}>
        <div className={styles.label}>{label ? formatMessage({ id: label.id }) : ''}</div>
        <div data-testid="conversion-bar-date" className={styles.date}>
          {dateString}
        </div>
      </div>
      <div data-testid="cb-cta-area" className={styles.ctaButtonWrapper}>
        {showSalesEndedDonationPanel ? <CheckoutButton isRemindMe /> : <ExploreSimilarEventsButton />}
      </div>
    </div>
  );
};
